// src/pages/ProductDetailPage.js
import { useParams, useNavigate, useLocation } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { useCart } from "../components/CartContext";
import { useProductData, productImageAlt, productInStock } from "../context/ProductDataContext";
import Zoom from "react-medium-image-zoom";
import { motion } from "framer-motion";
import CategoryBreadcrumb from "../components/category_page/CategoryBreadcrumb";
import SpecificationTable from "../components/product/SpecificationTable";
import "react-medium-image-zoom/dist/styles.css";
import { canon } from "../lib/seo";

// Markdown render biztonságosan
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";


const PRODUCT_MEDIA_BASE = "/media/products";

const normalizeImageUrl = (rawUrl, productId) => {
  const value = String(rawUrl || "").trim();
  if (!value) return "/img/placeholder.webp";

  if (/^https?:\/\//i.test(value) || value.startsWith("/")) {
    return value;
  }

  if (value.startsWith("media/")) {
    return `/${value}`;
  }

  const id = String(productId || "").trim();
  if (id) {
    return `${PRODUCT_MEDIA_BASE}/${encodeURIComponent(id)}/${value}`;
  }

  return value;
};

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { byId, loading, categories, loadFullProduct } = useProductData();
  const { addToCart, openCart } = useCart();

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [qty, setQty] = useState(1); // mennyiség a kosárhoz (1–99)
  const [fullProduct, setFullProduct] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { pathname } = useLocation();

  const catalogProduct = useMemo(
    () => (productId ? byId.get(productId) || null : null),
    [byId, productId]
  );

  useEffect(() => {
    let alive = true;

    async function loadDetail() {
      setFullProduct(null);
      if (loading) return;
      if (!catalogProduct && !productId) return;

      setDetailLoading(true);
      try {
        const full = await loadFullProduct(catalogProduct || productId);
        if (alive) setFullProduct(full);
      } finally {
        if (alive) setDetailLoading(false);
      }
    }

    loadDetail();
    return () => {
      alive = false;
    };
  }, [loading, catalogProduct, productId, loadFullProduct]);

  const product = fullProduct || catalogProduct;

  // --- Képgaléria: detail media.images, különben index image ---
  const imageList = useMemo(() => {
    const productKey = product?.id || productId;
    const fromMedia = Array.isArray(product?.media?.images) ? product.media.images : [];

    if (fromMedia.length > 0) {
      return fromMedia
        .map((img, index) => ({
          src: normalizeImageUrl(img?.url, productKey),
          alt: img?.alt || product?.name || `Termékkép ${index + 1}`,
          isPrimary: !!img?.isPrimary,
        }))
        .filter((img) => img.src)
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
    }

    if (product?.image) {
      return [{
        src: product.image,
        alt: productImageAlt(product),
        isPrimary: true,
      }];
    }

    return [{ src: "/img/placeholder.webp", alt: "placeholder", isPrimary: true }];
  }, [product, productId]);

  useEffect(() => {
    setSelectedImageIndex((prev) => {
      if (!imageList.length) return 0;
      return Math.min(Math.max(prev, 0), imageList.length - 1);
    });
  }, [imageList.length]);

  const selectedImage = imageList[selectedImageIndex] || imageList[0];

  const showPrevImage = () => {
    setSelectedImageIndex((prev) =>
      imageList.length ? (prev - 1 + imageList.length) % imageList.length : 0
    );
  };

  const showNextImage = () => {
    setSelectedImageIndex((prev) =>
      imageList.length ? (prev + 1) % imageList.length : 0
    );
  };

  // --- Kategória útvonal meghatározása breadcrumbhoz ---
  const categoryId = product?.categoryId;

  const findCategory = (id, list) => {
    for (let item of list || []) {
      if (item.id === id) return item;
      if (item.children?.length) {
        const found = findCategory(id, item.children);
        if (found) return found;
      }
    }
    return null;
  };
  const currentCategory = findCategory(categoryId, categories || []);

  // --- SEO (React 19 natív metadata hoisting) ---
  const title = product?.seo?.metaTitle || product?.name || "Termék";
  const description =
    product?.seo?.metaDescription ||
    product?.subtitle ||
    (typeof product?.description === "string"
      ? product.description.replace(/[#*_>`\[\]\n]/g, " ").slice(0, 150)
      : "");
  const images = imageList.map((img) => img.src).filter(Boolean);
  const ogImage = images[0] || "/img/placeholder.webp";
  const canonical = canon(pathname);
  const availability = productInStock(product) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";

  // ---- JSON-LD kiegészítések (Google ajánlások) ----
  // Árak érvényessége: +90 nap (YYYY-MM-DD)
  const priceValidUntil = undefined;

  // Alap visszaküldési politika (HU, 14 nap)
  const merchantReturnPolicy = {
    "@type": "MerchantReturnPolicy",
    applicableCountry: "HU",
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 14,
  };

  // Alap szállítási információk (HU, fix díj példa)
  const offerShippingDetails = {
    "@type": "OfferShippingDetails",
    shippingRate: {
      "@type": "MonetaryAmount",
      value: "1490",
      currency: "HUF",
    },
    shippingDestination: {
      "@type": "DefinedRegion",
      addressCountry: "HU",
    },
  };

  // (Opcionális) Alap összesített értékelés + egy minta review
  // Ha nem szeretnéd, könnyen kikapcsolható vagy feltételhez köthető.
  const aggregateRating = undefined;
  const productLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product?.name,
    image: images,
    description,
    brand: { "@type": "Brand", name: product?.brand },
    sku: product?.id,
    offers: {
      "@type": "Offer",
      priceCurrency: "HUF",
      price: typeof product?.price === "number" ? product.price : undefined,
      availability,
      url: canonical,
      priceValidUntil,
      hasMerchantReturnPolicy: merchantReturnPolicy,
      shippingDetails: offerShippingDetails,
    },
    aggregateRating,
    review: [],
  };

  if (loading || (detailLoading && !product)) return <div className="p-6">Betöltés...</div>;
  if (!product) return <div className="p-6 text-red-600">A termék nem található.</div>;

  const priceText =
    typeof product.price === "number"
      ? product.price.toLocaleString("hu-HU")
      : product.price ?? "";

  const inStock = productInStock(product);

  // --- META adatokhoz biztonságos értékek ---
  const mfr =
    product?.identifiers?.sku ||
    product?.identifiers?.model ||
    product?.identifiers?.mpn ||
    product?.identifiers?.ean ||
    "Nincs információ";
  const brandName = product?.brand ?? "Nincs információ";
  const warrantyText = product?.warranty?.periodMonths ?? "Nincs információ";
  const fullDescription =
    typeof product?.description === "string" ? product.description.trim() : "";


  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      {/* === SEO metadata (React 19 hoisting) === */}
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="product" />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
      <script type="application/ld+json">{JSON.stringify(productLd)}</script>

      {/* ===== HERO – egységes, HomePage-stílus (GOMBOK NÉLKÜL) ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c1a26] to-[#173047]" />
        <div className="absolute inset-0 opacity-25">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${"/img/t5.webp"})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-20 md:pb-28 text-white">
          <div className="text-sm uppercase tracking-wide text-white/80">Termék</div>
          <h1 className="text-3xl md:text-5xl font-bold mt-1 drop-shadow-sm">{product.name}</h1>
          {product.subtitle && (
            <p className="text-white/90 text-lg md:text-xl max-w-3xl mt-2">
              {product.subtitle}
            </p>
          )}
        </div>
      </section>

      {/* ===== TARTALOM ===== */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Breadcrumb felül, egységes helyen */}
        <div className="mb-4">
          <CategoryBreadcrumb
            currentCategory={currentCategory}
            categories={categories}
            productName={product.name}
          />
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-[1.05fr_1fr] gap-6 items-start"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          {/* BAL HASÁB: képek + specifikációk */}
          <motion.section
            className="min-w-0"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Képkártya */}
            <div className="bg-white/90 backdrop-blur rounded-2xl border p-3 md:p-4">
              {selectedImage && (
                <div className="relative">
                  <Zoom key={selectedImage.src}>
                    <motion.img
                      src={selectedImage.src}
                      alt={selectedImage.alt || product.name}
                      className="w-full aspect-square object-contain rounded-xl border bg-white"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.25 }}
                      onError={(e) => {
                        e.currentTarget.src = "/img/placeholder.webp";
                      }}
                    />
                  </Zoom>

                  {imageList.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={showPrevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border shadow hover:bg-white"
                        aria-label="Előző kép"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        onClick={showNextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border shadow hover:bg-white"
                        aria-label="Következő kép"
                      >
                        ›
                      </button>
                    </>
                  )}
                </div>
              )}

              {imageList.length > 1 && (
                <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                  {imageList.map((img, i) => (
                    <button
                      key={`${img.src}-${i}`}
                      type="button"
                      onClick={() => setSelectedImageIndex(i)}
                      className={`aspect-square rounded-lg border bg-white p-1 shadow-sm transition ${
                        i === selectedImageIndex
                          ? "border-blue-600 ring-2 ring-blue-200"
                          : "border-gray-200 hover:border-blue-300"
                      }`}
                      aria-label={`${i + 1}. termékkép megnyitása`}
                    >
                      <motion.img
                        src={img.src}
                        alt={img.alt || `${product.name} kép ${i + 1}`}
                        loading="lazy"
                        className="h-full w-full object-contain rounded-md"
                        whileHover={{ scale: 1.04 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        onError={(e) => {
                          e.currentTarget.src = "/img/placeholder.webp";
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Specifikációk kártya */}
            <SpecificationTable product={product} categoryId={categoryId} />
          </motion.section>

          {/* JOBB HASÁB: terméknév, ár, mennyiség, gombok, META, leírás */}
          <motion.section
            className="min-w-0 space-y-4"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Árkártya + vezérlők */}
            <div className="bg-white/90 backdrop-blur rounded-2xl border p-4">
              <h2 className="text-xl font-bold mb-2">{product.name}</h2>

              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm text-gray-600">Ár</div>
                  <div className="text-2xl font-bold tabular-nums">
                    {priceText ? `${priceText} Ft` : "—"}
                  </div>
                </div>
                <div
                  className={`px-2 py-1 rounded text-sm ${
                    inStock
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-amber-50 text-amber-700 border border-amber-200"
                  }`}
                >
                  {inStock ? "Raktáron" : "Rendelésre"}
                </div>
              </div>

              {/* Mennyiség vezérlő */}
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  className="h-10 w-10 border rounded hover:bg-gray-50"
                  onClick={() =>
                    setQty((q) => Math.max(1, Math.min(99, Number(q) - 1 || 1)))
                  }
                  aria-label="Egy darabbal kevesebb"
                  title="−"
                >
                  −
                </button>

                <input
                  type="number"
                  min={1}
                  max={99}
                  value={qty}
                  onChange={(e) =>
                    setQty(Math.max(1, Math.min(99, Number(e.target.value) || 1)))
                  }
                  className="h-10 w-20 border rounded text-center"
                  aria-label="Mennyiség"
                  inputMode="numeric"
                />

                <button
                  type="button"
                  className="h-10 w-10 border rounded hover:bg-gray-50"
                  onClick={() =>
                    setQty((q) => Math.max(1, Math.min(99, Number(q) + 1 || 1)))
                  }
                  aria-label="Egy darabbal több"
                  title="+"
                >
                  +
                </button>
              </div>

              {/* Gombsor */}
              <div className="mt-4 flex items-center gap-3">
                <button
                  className="h-11 px-5 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => {
                    const n = Math.max(1, Math.min(99, Number(qty) || 1));
                    addToCart(product, n);
                    openCart();
                  }}
                  aria-label="Kosárba helyezés"
                >
                  Kosárba
                </button>

                <button
                  className="h-11 px-5 rounded-xl border hover:bg-gray-50"
                  onClick={() => navigate("/kategoriak")}
                >
                  Tovább böngészek
                </button>
              </div>
            </div>

            {/* === META adatok kártya (új) === */}
            <div className="bg-white/90 backdrop-blur rounded-2xl border p-4">
              <h3 className="text-lg font-semibold mb-2">Általános információ</h3>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-t first:border-t-0">
                    <td className="py-2 pr-3 font-medium w-36">Cikkszám</td>
                    <td className="py-2">{mfr}</td>
                  </tr>
                  <tr className="border-t">
                    <td className="py-2 pr-3 font-medium">Márka</td>
                    <td className="py-2">{brandName}</td>
                  </tr>
                  <tr className="border-t">
                    <td className="py-2 pr-3 font-medium">Garancia (Hónap)</td>
                    <td className="py-2">{warrantyText}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Leírás (Markdown) */}
            {detailLoading && !fullDescription && (
              <div className="bg-white/90 backdrop-blur rounded-2xl border p-4 text-sm text-gray-600">
                Részletes termékadatok betöltése…
              </div>
            )}

            {fullDescription && (
              <div className="bg-white/90 backdrop-blur rounded-2xl border p-4">
                <h3 className="text-lg font-semibold mb-2">Leírás</h3>
                <div className="prose prose-zinc prose-headings:font-semibold prose-h2:text-lg prose-h3:text-base prose-a:text-blue-600 hover:prose-a:text-blue-700 prose-strong:text-gray-800 prose-img:rounded-lg max-w-none leading-relaxed break-words">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                    {fullDescription}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </motion.section>
        </motion.div>
      </div>
    </div>
  );
}
